import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Loader2,
  Paperclip,
  Upload,
  Trash2,
  Download,
  FileSpreadsheet,
  FileText as FileTextIcon,
  File as FileIcon,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { API_BASE_URL } from "@/config/api";

const API = `${API_BASE_URL}/documents`;
const ALLOWED_EXT = [".pdf", ".xls", ".xlsx", ".csv"];
const ACCEPT = ALLOWED_EXT.join(",");
const MAX_BYTES = 25 * 1024 * 1024;

function formatBytes(n) {
  if (!n && n !== 0) return "";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function iconFor(filename = "", contentType = "") {
  const ext = filename.toLowerCase().split(".").pop();
  if (ext === "pdf" || /pdf/.test(contentType)) {
    return <FileTextIcon className="w-4 h-4 text-[#D32F2F]" />;
  }
  if (["xls", "xlsx", "csv"].includes(ext) || /sheet|excel|csv/.test(contentType)) {
    return <FileSpreadsheet className="w-4 h-4 text-[#388E3C]" />;
  }
  return <FileIcon className="w-4 h-4 text-[#617364]" />;
}

export default function DocumentAttachments({ docId, canEdit }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const load = async () => {
    if (!docId) return;
    try {
      setLoading(true);
      const res = await axios.get(`${API}/${docId}/attachments`);
      setItems(res.data || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load attachments");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [docId]);

  const handleFiles = async (files) => {
    if (!files || files.length === 0) return;
    const arr = Array.from(files);

    // Pre-validate
    for (const f of arr) {
      const ext = "." + (f.name.split(".").pop() || "").toLowerCase();
      if (!ALLOWED_EXT.includes(ext)) {
        toast.error(`"${f.name}" is not supported. Allowed: PDF, XLS, XLSX, CSV.`);
        return;
      }
      if (f.size > MAX_BYTES) {
        toast.error(`"${f.name}" exceeds the 25 MB limit.`);
        return;
      }
    }

    setUploading(true);
    let ok = 0;
    for (const f of arr) {
      const fd = new FormData();
      fd.append("file", f);
      try {
        // eslint-disable-next-line no-await-in-loop
        await axios.post(`${API}/${docId}/attachments`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        ok += 1;
      } catch (err) {
        toast.error(err?.response?.data?.detail || `Failed to upload "${f.name}"`);
      }
    }
    setUploading(false);
    if (ok > 0) toast.success(`Uploaded ${ok} file${ok === 1 ? "" : "s"}`);
    if (fileInputRef.current) fileInputRef.current.value = "";
    load();
  };

  const handleDelete = async (att) => {
    if (!window.confirm(`Remove "${att.filename}"?`)) return;
    try {
      await axios.delete(`${API}/attachments/${att.id}`);
      toast.success("Attachment removed");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to remove");
    }
  };

  const handleDownload = async (att) => {
    try {
      const res = await axios.get(`${API}/attachments/${att.id}/download`, {
        responseType: "blob",
      });
      const blob = new Blob([res.data], {
        type: att.content_type || "application/octet-stream",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = att.filename || "download";
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to download");
    }
  };

  return (
    <Card className="mt-6 border-[#E0E6E1] bg-white p-5 sm:p-6" data-testid="doc-attachments">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-[#43A047]/12 flex items-center justify-center">
            <Paperclip className="w-4 h-4 text-[#43A047]" />
          </div>
          <div>
            <h3
              className="text-base font-semibold text-[#1B291D] tracking-tight"
              style={{ fontFamily: "Work Sans" }}
            >
              Attachments
            </h3>
            <p className="text-[11px] text-[#617364] tracking-wide">
              PDF, XLS, XLSX, CSV · up to 25 MB each
            </p>
          </div>
        </div>
        {canEdit && (
          <>
            <input
              ref={fileInputRef}
              type="file"
              accept={ACCEPT}
              multiple
              className="hidden"
              onChange={(e) => handleFiles(e.target.files)}
              data-testid="doc-attachment-file-input"
            />
            <Button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="bg-[#1B291D] hover:bg-[#2a3d2e] text-white"
              data-testid="doc-attachment-upload"
            >
              {uploading ? (
                <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
              ) : (
                <Upload className="w-4 h-4 mr-1.5" />
              )}
              {uploading ? "Uploading..." : "Upload files"}
            </Button>
          </>
        )}
      </div>

      {loading ? (
        <div className="py-6 flex justify-center">
          <Loader2 className="w-5 h-5 animate-spin text-[#43A047]" />
        </div>
      ) : items.length === 0 ? (
        <div
          className="border border-dashed border-[#E0E6E1] rounded-lg py-6 text-center"
          data-testid="doc-attachments-empty"
        >
          <Paperclip className="w-5 h-5 text-[#617364] mx-auto opacity-50 mb-1" />
          <p className="text-xs text-[#617364]">
            {canEdit
              ? "No attachments yet. Click \u201CUpload files\u201D to add PDFs or Excel files."
              : "No attachments uploaded for this document."}
          </p>
        </div>
      ) : (
        <ul className="divide-y divide-[#E0E6E1]">
          {items.map((a) => (
            <li
              key={a.id}
              className="py-2.5 flex items-center gap-3"
              data-testid={`doc-attachment-${a.id}`}
            >
              <div className="w-8 h-8 rounded-md bg-[#F5F9F6] flex items-center justify-center flex-shrink-0">
                {iconFor(a.filename, a.content_type)}
              </div>
              <button
                onClick={() => handleDownload(a)}
                className="min-w-0 flex-1 text-left group"
                title="Download"
                data-testid={`doc-attachment-download-${a.id}`}
              >
                <div className="text-sm text-[#1B291D] font-medium truncate group-hover:text-[#43A047] transition-colors">
                  {a.filename}
                </div>
                <div className="text-[11px] text-[#617364] tracking-wide">
                  {formatBytes(a.size_bytes)}
                  {a.uploaded_by ? ` · ${a.uploaded_by}` : ""}
                  {a.uploaded_at
                    ? ` · ${new Date(a.uploaded_at).toLocaleDateString()}`
                    : ""}
                </div>
              </button>
              <button
                onClick={() => handleDownload(a)}
                className="w-8 h-8 rounded-md flex items-center justify-center text-[#617364] hover:bg-[#43A047]/10 hover:text-[#43A047]"
                title="Download"
                data-testid={`doc-attachment-dl-btn-${a.id}`}
              >
                <Download className="w-3.5 h-3.5" />
              </button>
              {canEdit && (
                <button
                  onClick={() => handleDelete(a)}
                  className="w-8 h-8 rounded-md flex items-center justify-center text-[#617364] hover:bg-[#D32F2F]/10 hover:text-[#D32F2F]"
                  title="Remove"
                  data-testid={`doc-attachment-delete-${a.id}`}
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}
